import { Core, Decorative } from "@faire/design-tokens";
import { Button } from "@faire/web--source/slate/Button";
import { Row } from "@faire/web--source/slate/Layout";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDetectScroll } from "@faire/web--source/ui/hooks/useDetectScroll";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackR2BReferralsClicksOnSignUpToSellButtonClick } from "@faire/web-api--source/events/r2BReferrals/click/clicksOnSignUpToSellButton";
import brands from "@faire/web-api--source/routes/brands";
import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

const FAIRE_LOGO_WIDTH = 120;
const CTA_CONTAINER_HEIGHT = 40;
const VERTICAL_PADDING = 16;
const HEADER_HEIGHT = 2 * VERTICAL_PADDING + CTA_CONTAINER_HEIGHT;
const TOP_SECTION_HEIGHT = 490;

export const Header: React.FC<{
  showCTA: boolean;
  setShowCTA: (value: boolean) => void;
}> = ({ showCTA, setShowCTA }) => {
  const { isTabletAndAbove, isDesktopAndAbove } = useViewport();
  const [hasScrolled, setHasScrolled] = useState(false);

  const { scrollPosition } = useDetectScroll();
  useEffect(() => {
    if (isTabletAndAbove) {
      if (!showCTA && scrollPosition > HEADER_HEIGHT + TOP_SECTION_HEIGHT) {
        setHasScrolled(true);
        setShowCTA(true);
      } else if (
        showCTA &&
        scrollPosition < HEADER_HEIGHT + TOP_SECTION_HEIGHT
      ) {
        setShowCTA(false);
      }
    }
  }, [isTabletAndAbove, scrollPosition, showCTA, setShowCTA]);

  return (
    <Container>
      <Button variant="tertiary" linkTo={brands()}>
        <FaireLogo inverse size={FAIRE_LOGO_WIDTH} />
      </Button>
      {hasScrolled && isTabletAndAbove ? (
        <CTAContainer {...{ $showCTA: showCTA }}>
          {isDesktopAndAbove ? (
            <>
              <Typography color={Core.surface.inverse}>
                <StrictLocalMsg
                  defaultMessage="Connect with our team to learn about your potential on Faire."
                  description={{
                    text: "Prompts the brand to reach out to the Faire team",
                  }}
                />
              </Typography>
              <Spacer width="5x" />
            </>
          ) : null}
          <SignUpButton
            tertiaryColor={Core.text.inverse}
            onClick={trackR2BReferralsClicksOnSignUpToSellButtonClick}
          >
            <StrictLocalMsg
              defaultMessage="Sign up to sell"
              description={{ text: "Prompts the brand to sign up for Faire" }}
            />
          </SignUpButton>
        </CTAContainer>
      ) : null}
    </Container>
  );
};

const Container = styled(Row).attrs({
  align: "center",
  justify: { mobileAndAbove: "center", tabletAndAbove: "space-between" },
})`
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  background-color: ${Decorative.shade.yellow};
  padding: ${getSpacing(VERTICAL_PADDING, "10x")};

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(VERTICAL_PADDING, "3x")};
  }
`;

const slideUpAnimation = keyframes`
  0% {
    transform: translateY(${VERTICAL_PADDING}px);
  }
  100% {
    transform: translateY(0);
  }
`;

const slideDownAnimation = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(${VERTICAL_PADDING}px);
    visibility: hidden;
  }
`;

const CTAContainer = styled(Row).attrs({
  align: "center",
})<{ $showCTA: boolean }>`
  animation: ${({ $showCTA: showCTA }) =>
      showCTA ? slideUpAnimation : slideDownAnimation}
    0.3s ease-in-out;
  animation-fill-mode: both;
  height: ${CTA_CONTAINER_HEIGHT}px;
`;

const SignUpButton = styled(Button).attrs({
  variant: "tertiary",
  target: "_blank",
})`
  width: max-content;
`;
